// Brand reference-deck inspiration. The agentic deck build gathers the brand
// reference deck (via the temporary insert -> extract -> strip path in
// `parsePptxBase64AsReference`) and feeds its slides to the agent as the brand
// visual standard: one structured example per slide (serialized shapes + an
// optional rendered image), interleaved with the image by the OpenAI-native
// provider.

import type { PresentationContent, ShapeContent } from "../types";
import type { ReferenceDeckExamples } from "../services/ai/types";

function serializeShape(s: ShapeContent): Record<string, unknown> {
  const obj: Record<string, unknown> = {
    type: s.type,
    left: s.left,
    top: s.top,
    width: s.width,
    height: s.height,
  };
  if (s.text) obj.text = s.text.length > 500 ? s.text.slice(0, 500) + "…[truncated]" : s.text;
  if (s.geometricType) obj.geometricType = s.geometricType;
  if (s.fillColor) obj.fillColor = s.fillColor;
  if (s.lineColor) obj.lineColor = s.lineColor;
  if (s.lineWidth) obj.lineWidth = s.lineWidth;
  if (s.fontSize) obj.fontSize = s.fontSize;
  if (s.fontFamily) obj.fontFamily = s.fontFamily;
  if (s.textColor) obj.textColor = s.textColor;
  if (s.bold !== undefined) obj.bold = s.bold;
  if (s.italic) obj.italic = s.italic;
  if (s.horizontalAlignment) obj.horizontalAlignment = s.horizontalAlignment;
  if (s.verticalAlignment) obj.verticalAlignment = s.verticalAlignment;
  if (s.paragraphs) obj.paragraphs = s.paragraphs;
  if (s.tableData) obj.tableData = s.tableData;
  return obj;
}

/**
 * Build the structured "REFERENCE DECK EXAMPLES" block: a shared intro, one
 * entry per reference slide (title label + serialized shape JSON + the slide's
 * rendered image when available), and a shared outro. The OpenAI-native provider
 * interleaves each slide's JSON with its image so the model can match the brand's
 * positioning grid, typography, and palette against the actual rendered slide.
 * `images[i]` aligns with reference slide `i` (undefined past the render cap).
 */
export function buildReferenceDeckExamples(
  deck: PresentationContent,
  images: Array<string | undefined> = []
): ReferenceDeckExamples {
  const intro = `REFERENCE DECK EXAMPLES (HIGH PRIORITY — match this style, layout, and structure):
Below are real slides extracted from a reference presentation. Replicate the positioning patterns, font sizes, font families, colors, spacing, and layout structure shown here. These examples define the visual standard for this deck.
Slide dimensions: ${deck.slideWidth ?? "unknown"} x ${deck.slideHeight ?? "unknown"}`;

  const slides = deck.slides.map((slide, idx) => {
    const titleShape = slide.shapes
      .filter((s) => s.type === "textBox" && s.text)
      .sort((a, b) => (b.fontSize ?? 0) - (a.fontSize ?? 0))[0];
    const title = titleShape?.text?.split("\n")[0] || `Slide ${idx + 1}`;
    return {
      label: `=== Slide ${idx + 1}: "${title}" ===`,
      json: JSON.stringify(slide.shapes.map(serializeShape), null, 2),
      image: images[idx],
    };
  });

  const outro = `IMPORTANT: Use these examples as your primary style guide. Match font sizes, positioning grid, color palette, and shape usage patterns exactly. The image shown with each slide's spec is that same reference slide — match its layout, color, and visual hierarchy.`;

  return { intro, slides, outro };
}

/**
 * Build the curated brand-reference block: an anchored screenshot per selected
 * page with an authored caption and no shape JSON. Brands using this mode keep
 * their numeric geometry in the brand style prompt, so serializing the template
 * would only add noise (the UBS deck alone serializes to ~2M characters, most of
 * it `tableData`, which biases the model toward table-shaped output).
 * `images[i]` aligns with `slides[i]`.
 */
export function buildCuratedReferenceExamples(
  slides: Array<{ anchor: string; caption: string }>,
  images: Array<string | undefined> = []
): ReferenceDeckExamples {
  const intro = `BRAND REFERENCE PAGES (HIGH PRIORITY — match this visual standard):
Below are rendered pages from the brand's own sample deck, each labelled with an anchor id. The brand style rules cite these anchors by name: when a rule references an anchor, match that page's composition, density, and whitespace rhythm.
No shape JSON is attached — the exact geometry you need is stated numerically in the brand style rules.`;

  const entries = slides.map((slide, idx) => ({
    label: `=== ${slide.anchor} ===`,
    caption: slide.caption,
    image: images[idx],
  }));

  const outro = `IMPORTANT: These pages define the brand's visual standard. Reproduce their structure and restraint; do not copy their sample content.`;

  return { intro, slides: entries, outro };
}

/**
 * Build the "BASE SLIDE TEMPLATES" block for user-selected base slides
 * (existing-deck DUPLICATE or slide-library LIBRARY). Unlike the brand reference
 * examples, these are the EXACT slides the agent will duplicate/insert and fill
 * in place. Callers pass pre-assembled entries (full shape JSON + rendered image)
 * so this helper only wraps them with the shared intro/outro. The OpenAI-native
 * provider interleaves each entry's JSON with its image.
 */
export function buildBaseTemplateExamples(
  slides: ReferenceDeckExamples["slides"]
): ReferenceDeckExamples {
  const intro = `BASE SLIDE TEMPLATES (HIGH PRIORITY — these are the exact slides you will duplicate (existing-deck) or insert (slide-library) and then FILL IN PLACE):
Each entry is a selected base slide's shape spec (JSON) immediately followed by its rendered image. Match each one's layout, structure, spacing, and styling; change only the content to that slide's plan. Clone the template's repeating elements to fit additional planned content, but keep their styling and the overall layout.`;

  const outro = `IMPORTANT: Do NOT invent a new layout for these slides. Duplicate/insert the named base slide and edit its EXISTING shapes in place so the result matches the spec + image above.`;

  return { intro, slides, outro };
}
